import type {
  ChartSpec,
  CopilotUiMessage,
  TableColumnType,
  TableSpec
} from './copilot.types';

const PEOPLE_IN_LABEL = /(\d+)\s+people\b/i;

export function peopleCountOnMessage(message: CopilotUiMessage): number {
  const fromLabel = PEOPLE_IN_LABEL.exec(message.conversationScope?.label ?? '');
  const labeled = fromLabel ? Number(fromLabel[1]) : 0;
  return Math.max(
    labeled,
    message.conversationScope?.users?.length ?? 0,
    message.charts?.[0]?.categories?.length ?? 0,
    message.tables?.[0]?.rows?.length ?? 0,
  );
}

/** True only when we know this card is a single person. Unknown → treat as team. */
export function isSinglePersonCard(message: CopilotUiMessage): boolean {
  const n = peopleCountOnMessage(message);
  return n === 1;
}

function columnTypeForChart(chart: ChartSpec): TableColumnType {
  if (chart.unit === 'hours' || chart.unit === 'minutes') {
    return 'duration';
  }
  if (chart.unit === 'percent') {
    return 'percent';
  }
  return 'number';
}

function durationLabel(label: string): string {
  return label.replace(/\s*(minutes?|hours?|mins?|secs?)\s*$/i, ' time').trim();
}

function secondsFromChartValue(value: number, unit: ChartSpec['unit']): number {
  if (unit === 'hours') {
    return value * 3600;
  }
  if (unit === 'minutes') {
    return value * 60;
  }
  return value;
}

export function tableFromChart(chart: ChartSpec): TableSpec | null {
  const series = chart.series?.[0];
  if (!chart.categories?.length || !series?.values?.length) {
    return null;
  }
  const type = columnTypeForChart(chart);
  const rawLabel = series.label || chart.unit || 'Value';
  const label = type === 'duration' ? durationLabel(rawLabel) : rawLabel;
  return {
    id: `${chart.id}_people`,
    title: chart.title,
    subtitle: chart.subtitle,
    columns: [
      { key: 'person', label: 'Person', type: 'text', sortable: true },
      { key: 'value', label, type, sortable: true },
    ],
    rows: chart.categories.map((person, i) => ({
      person,
      value:
        type === 'duration'
          ? secondsFromChartValue(series.values[i], chart.unit)
          : series.values[i],
      ...(series.formattedValues?.[i]
        ? { valueFormatted: series.formattedValues[i] }
        : {}),
    })),
    defaultSort: { column: 'value', direction: 'desc' },
    sourceTool: chart.sourceTool,
  };
}

export function peopleTablesForMessage(message: CopilotUiMessage): TableSpec[] {
  if (message.tables?.length) {
    return message.tables;
  }
  const derived = message.charts?.[0] ? tableFromChart(message.charts[0]) : null;
  return derived ? [derived] : [];
}

export function shortFormatToggleFlags(
  message: CopilotUiMessage,
): { trend: boolean, people: boolean, approach: boolean } | null {
  const approach =
    !!message.shortFormat && !!message.coachingSuggestions?.length;
  if (isSinglePersonCard(message)) {
    return approach ? { trend: false, people: false, approach: true } : null;
  }
  const trend = !!message.charts?.length;
  const people = peopleTablesForMessage(message).length > 0;
  if (!trend && !people && !approach) {
    return null;
  }
  return { trend, people, approach };
}
